import {
  CareOrderStatusEnum,
  filterExistsAndExcludes,
  IconName,
  isExcludedByCurrencyOrBasePairFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useGenericFilter,
  useSecuritiesContext,
  useSidesFilter,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type CareOrder,
  type CptySettlementTypeEnum,
  type DateRangeFilter,
  type FilterableProperty,
  type GenericFilterFilterProps,
  type Leaves,
} from '@talos/kyoko';
import { startCase, values } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { passesCareOrderDateRangeFilter } from '../careOrderDateRangeFilter';
import type { CareOrderTimestampField } from '../careOrderTimestampFields';
import { useCptySettlementTypeFilter } from '../useCptySettlementTypeFilter';

interface CareOrderManagementFilter extends DateRangeFilter<CareOrderTimestampField> {
  Statuses?: string[];
  Symbols?: string[];
  Sides?: string[];
  OriginationTrader?: string[];
  CptySettlementTypes?: CptySettlementTypeEnum[];
  Currencies?: string[];
  BasePairs?: string[];
}

interface UseCareOrderManagementFiltersParams {
  filterProps: GenericFilterFilterProps<CareOrderManagementFilter>;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export const useCareOrderManagementFilters = ({
  filterProps,
  includeOpenCareOrdersOutsideDateRange = true,
}: UseCareOrderManagementFiltersParams) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const sidesFilter = useSidesFilter();
  const symbolsFilter = useSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const cptySettlementTypeFilter = useCptySettlementTypeFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      sidesFilter,
      {
        key: 'Statuses',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: values(CareOrderStatusEnum),
        getOptionLabel: startCase,
      },
      {
        key: 'OriginationTrader',
        label: 'Origination Trader',
        icon: IconName.User,
        control: 'text',
      },
      cptySettlementTypeFilter,
      symbolsFilter,
      currencyOfSymbolFilter,
      basePairFilter,
    ],
    [cptySettlementTypeFilter, sidesFilter, symbolsFilter, currencyOfSymbolFilter, basePairFilter]
  );

  const genericFilterResults = useGenericFilter<CareOrderManagementFilter>(filterableProperties, filterProps);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<CareOrder>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }

      if (!passesCareOrderDateRangeFilter(data, genericFilterResults.filter, includeOpenCareOrdersOutsideDateRange)) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter, 'Statuses', data, 'status')) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter, 'Symbols', data, 'Symbol')) {
        return false;
      }

      if (
        isExcludedByCurrencyOrBasePairFilters(
          data.Symbol,
          securitiesBySymbol,
          genericFilterResults.filter.Currencies,
          genericFilterResults.filter.BasePairs
        )
      ) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter, 'Sides', data, 'Side')) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter, 'OriginationTrader', data, 'originationTrader')) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter, 'CptySettlementTypes', data, 'CptySettlementType')) {
        return false;
      }

      return true;
    },
    [genericFilterResults.filter, includeOpenCareOrdersOutsideDateRange, securitiesBySymbol]
  );

  return { ...genericFilterResults, filterableProperties, clientSideFilter };
};

const colIDToFilterBuilderKeyMap = {
  status: 'Statuses',
  Symbol: 'Symbols',
  Side: 'Sides',
  CptySettlementType: 'CptySettlementTypes',
} satisfies Partial<Record<Leaves<CareOrder>, keyof CareOrderManagementFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof CareOrderManagementFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}
